<template>
  <component
    :is="iconComponent"
    v-if="iconComponent"
    :class="iconClasses"
    v-bind="iconProps"
  />
  <svg
    v-else-if="customSvg"
    :class="iconClasses"
    v-html="customSvg"
  />
  <span
    v-else-if="emoji"
    :class="emojiClasses"
    role="img"
    :aria-label="ariaLabel"
  >
    {{ emoji }}
  </span>
  <div
    v-else
    :class="placeholderClasses"
    :title="name || 'Ícone não encontrado'"
  >
    <svg
      class="w-full h-full"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
type IconColor = 'current' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'gray' | 'white'

const props = withDefaults(defineProps<{
  name?: string
  size?: IconSize
  color?: IconColor
  customSvg?: string
  emoji?: string
  ariaLabel?: string
  clickable?: boolean
  spin?: boolean
  pulse?: boolean
}>(), {
  name: '',
  size: 'md',
  color: 'current',
  customSvg: '',
  emoji: '',
  ariaLabel: '',
  clickable: false,
  spin: false,
  pulse: false
})

const emit = defineEmits(['click'])

// Mapeamento de ícones comuns do projeto
const iconMap: Record<string, any> = {
  // Navegação
  'arrow-left': () => import('@vicons/ionicons5/ArrowBack'),
  'arrow-right': () => import('@vicons/ionicons5/ArrowForward'),
  'arrow-up': () => import('@vicons/ionicons5/ArrowUp'),
  'arrow-down': () => import('@vicons/ionicons5/ArrowDown'),
  'chevron-left': () => import('@vicons/ionicons5/ChevronBack'),
  'chevron-right': () => import('@vicons/ionicons5/ChevronForward'),
  'chevron-up': () => import('@vicons/ionicons5/ChevronUp'),
  'chevron-down': () => import('@vicons/ionicons5/ChevronDown'),
  
  // Ações
  'plus': () => import('@vicons/ionicons5/Add'),
  'edit': () => import('@vicons/ionicons5/Create'),
  'delete': () => import('@vicons/ionicons5/Trash'),
  'save': () => import('@vicons/ionicons5/Save'),
  'cancel': () => import('@vicons/ionicons5/Close'),
  'check': () => import('@vicons/ionicons5/Checkmark'),
  'close': () => import('@vicons/ionicons5/Close'),
  'menu': () => import('@vicons/ionicons5/Menu'),
  'more': () => import('@vicons/ionicons5/EllipsisVertical'),
  'search': () => import('@vicons/ionicons5/Search'),
  'filter': () => import('@vicons/ionicons5/Funnel'),
  'refresh': () => import('@vicons/ionicons5/Refresh'),
  'download': () => import('@vicons/ionicons5/Download'),
  'upload': () => import('@vicons/ionicons5/CloudUpload'),
  'copy': () => import('@vicons/ionicons5/Copy'),
  'share': () => import('@vicons/ionicons5/Share'),
  
  // Status
  'loading': () => import('@vicons/ionicons5/Hourglass'),
  'success': () => import('@vicons/ionicons5/CheckmarkCircle'),
  'error': () => import('@vicons/ionicons5/CloseCircle'),
  'warning': () => import('@vicons/ionicons5/Warning'),
  'info': () => import('@vicons/ionicons5/InformationCircle'),
  
  // Usuário
  'user': () => import('@vicons/ionicons5/Person'),
  'users': () => import('@vicons/ionicons5/People'),
  'profile': () => import('@vicons/ionicons5/PersonCircle'),
  'login': () => import('@vicons/ionicons5/LogIn'),
  'logout': () => import('@vicons/ionicons5/LogOut'),
  
  // Conteúdo
  'file': () => import('@vicons/ionicons5/Document'),
  'folder': () => import('@vicons/ionicons5/Folder'),
  'folder-open': () => import('@vicons/ionicons5/FolderOpen'),
  'image': () => import('@vicons/ionicons5/Image'),
  'pdf': () => import('@vicons/ionicons5/DocumentText'),
  'link': () => import('@vicons/ionicons5/Link'),
  
  // Sistema
  'settings': () => import('@vicons/ionicons5/Settings'),
  'home': () => import('@vicons/ionicons5/Home'),
  'dashboard': () => import('@vicons/ionicons5/Grid'),
  'calendar': () => import('@vicons/ionicons5/Calendar'),
  'clock': () => import('@vicons/ionicons5/Time'),
  'mail': () => import('@vicons/ionicons5/Mail'),
  'phone': () => import('@vicons/ionicons5/Call'),
  'location': () => import('@vicons/ionicons5/Location'),
  'lock': () => import('@vicons/ionicons5/LockClosed'),
  'unlock': () => import('@vicons/ionicons5/LockOpen'),
  'eye': () => import('@vicons/ionicons5/Eye'),
  'eye-off': () => import('@vicons/ionicons5/EyeOff'),
  'heart': () => import('@vicons/ionicons5/Heart'),
  'star': () => import('@vicons/ionicons5/Star'),
  'bookmark': () => import('@vicons/ionicons5/Bookmark'),
  'tag': () => import('@vicons/ionicons5/Pricetag'),
  
  // Específicos do projeto
  'project': () => import('@vicons/ionicons5/Briefcase'),
  'client': () => import('@vicons/ionicons5/Business'),
  'audit': () => import('@vicons/ionicons5/Analytics'),
  'invite': () => import('@vicons/ionicons5/PersonAdd'),
  'admin': () => import('@vicons/ionicons5/Shield'),
}

const iconComponent = computed(() => {
  if (!props.name || !iconMap[props.name]) return null
  return defineAsyncComponent(iconMap[props.name])
})

const iconClasses = computed(() => [
  // Tamanhos
  sizeClasses.value,
  // Cores
  colorClasses.value,
  // Estados
  props.clickable ? 'cursor-pointer hover:opacity-75 transition-opacity' : '',
  props.spin ? 'animate-spin' : '',
  props.pulse ? 'animate-pulse' : '',
  // Interatividade
  props.clickable ? 'select-none' : ''
])

const emojiClasses = computed(() => [
  sizeClasses.value,
  props.clickable ? 'cursor-pointer hover:opacity-75 transition-opacity select-none' : ''
])

const placeholderClasses = computed(() => [
  sizeClasses.value,
  'text-gray-400',
  props.clickable ? 'cursor-pointer hover:opacity-75 transition-opacity' : ''
])

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'xs': return 'w-3 h-3'
    case 'sm': return 'w-4 h-4'
    case 'md': return 'w-5 h-5'
    case 'lg': return 'w-6 h-6'
    case 'xl': return 'w-8 h-8'
    case '2xl': return 'w-10 h-10'
    case '3xl': return 'w-12 h-12'
    default: return 'w-5 h-5'
  }
})

const colorClasses = computed(() => {
  switch (props.color) {
    case 'current': return 'text-current'
    case 'primary': return 'text-pdca-600'
    case 'secondary': return 'text-gray-600'
    case 'success': return 'text-green-600'
    case 'warning': return 'text-amber-600'
    case 'danger': return 'text-red-600'
    case 'gray': return 'text-gray-400'
    case 'white': return 'text-white'
    default: return 'text-current'
  }
})

const iconProps = computed(() => ({
  onClick: props.clickable ? handleClick : undefined
}))

function handleClick(event: MouseEvent) {
  if (props.clickable) {
    emit('click', event)
  }
}
</script>

<style scoped>
/* Animações customizadas se necessário */
</style>
